import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Clock, Phone } from 'lucide-react';

const Showroom = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const whatsappLink = `https://wa.me/919535621900?text=${encodeURIComponent(
    "Hi Tilerra, I'd like to enquire about your tile collections."
  )}`;

  return (
    <section
      id="showroom"
      ref={sectionRef}
      data-testid="showroom-section"
      className="py-24 md:py-32 bg-matte-charcoal"
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs uppercase tracking-[0.2em] text-burgundy font-medium">
            Visit Us
          </span>
          <h2
            data-testid="showroom-heading"
            className="font-playfair text-3xl md:text-4xl lg:text-5xl text-stone-white mt-4"
          >
            Our Showroom
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Map */}
          <motion.div
            className="relative overflow-hidden h-[300px] md:h-[400px]"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <iframe
              title="Tilerra Showroom Location"
              data-testid="showroom-map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.8254895754!2d77.5913!3d12.9822!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1670c9b44e6d%3A0xf8dfc3e8517e4fe0!2sSt%20Johns%20Church%20Rd%2C%20Bengaluru%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1"
              className="w-full h-full border-0"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>

          {/* Info */}
          <motion.div
            className="flex flex-col justify-center"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
          >
            {/* Address */}
            <div className="flex gap-4 mb-8">
              <div className="w-12 h-12 bg-burgundy/20 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-burgundy" />
              </div>
              <div>
                <h3 className="font-playfair text-lg text-stone-white mb-2">
                  Address
                </h3>
                <p
                  data-testid="showroom-address"
                  className="text-warm-grey leading-relaxed"
                >
                  
                  41, St Johns Church Rd,
                  <br />
                  Bharati Nagar, Bengaluru,
                  <br />
                  Karnataka 560001
                </p>
              </div>
            </div>

            {/* Timings */}
            <div className="flex gap-4 mb-8">
              <div className="w-12 h-12 bg-burgundy/20 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-burgundy" />
              </div>
              <div>
                <h3 className="font-playfair text-lg text-stone-white mb-2">
                  Store Timings
                </h3>
                <p data-testid="showroom-timings" className="text-warm-grey">
                  Monday - Sunday: 10:00 AM - 7:00 PM
                </p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex gap-4 mb-10">
              <div className="w-12 h-12 bg-burgundy/20 flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-burgundy" />
              </div>
              <div>
                <h3 className="font-playfair text-lg text-stone-white mb-2">
                  Contact
                </h3>
                <a
                  href="tel:+919535621900"
                  data-testid="showroom-phone"
                  className="text-warm-grey hover:text-burgundy transition-colors"
                >
                  +91 95356 21900
                </a>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:+919535621900"
                data-testid="showroom-call-btn"
                className="flex items-center justify-center gap-2 px-6 py-3 border border-stone-white/30 text-stone-white text-xs uppercase tracking-[0.15em] font-medium hover:bg-stone-white/10 transition-all duration-300"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="showroom-whatsapp-btn"
                className="flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] text-white text-xs uppercase tracking-[0.15em] font-medium hover:bg-[#25D366]/90 transition-all duration-300"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Showroom;
